import { Flex, Spinner, Text } from '@chakra-ui/react';
import { Button, OutlineCard, useToast } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  fetchDefaultBucketsForRole,
  fetchJournalConfiguredBuckets
} from 'src/api/queries';
import {
  useDeleteJournalConfigurationBucket,
  useSaveJournalConfigurationBuckets
} from 'src/api/mutations';
import { Overlay } from 'src/components/Overlay';
import BucketToggleList from './BucketToggleList';

export type ModalMode = 'add' | 'edit';

export interface BucketFormModalProps {
  isOpen: boolean;
  mode: ModalMode;
  journalId: string;
  editingRole?: string;
  onClose: () => void;
  roleDisplayNames: Record<string, string> | undefined;
}

const BucketFormModal: React.FC<BucketFormModalProps> = ({
  isOpen,
  mode,
  journalId,
  editingRole,
  onClose,
  roleDisplayNames
}) => {
  const toast = useToast();
  const isEdit = mode === 'edit';

  const saveMutation = useSaveJournalConfigurationBuckets(journalId);
  const deleteMutation = useDeleteJournalConfigurationBucket(journalId);

  const [role, setRole] = useState('');
  const activeRole = isEdit ? editingRole ?? '' : role;

  const [savingBuckets, setSavingBuckets] = useState<Set<string>>(new Set());
  const [savingSubBuckets, setSavingSubBuckets] = useState<Set<string>>(
    new Set()
  );

  const [expandedBuckets, setExpandedBuckets] = useState<Set<string>>(
    new Set()
  );

  const { data: bucketsData, isLoading: isBucketsLoading } = useQuery({
    queryKey: ['journal-default-buckets', journalId, activeRole],
    queryFn: () => fetchDefaultBucketsForRole(journalId, activeRole),
    enabled: !!activeRole
  });

  const buckets = useMemo(() => bucketsData?.buckets ?? [], [bucketsData]);

  const { data: configuredData, isLoading: isConfiguredLoading } = useQuery({
    queryKey: ['journal-configuration-buckets', journalId],
    queryFn: () => fetchJournalConfiguredBuckets(journalId),
    enabled: !!activeRole
  });

  const isDataLoading =
    !!activeRole && (isBucketsLoading || isConfiguredLoading);

  const roleConfiguredBuckets = useMemo(
    () =>
      configuredData?.find(entry => entry.role === activeRole)?.buckets ?? [],
    [configuredData, activeRole]
  );

  const configuredBucketNames = useMemo(
    () => new Set(roleConfiguredBuckets.map(b => b.bucketName)),
    [roleConfiguredBuckets]
  );

  const configuredSubBucketsMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const bucket of roleConfiguredBuckets) {
      map.set(
        bucket.bucketName,
        new Set(bucket.subBuckets.map(sb => sb.subBucketName))
      );
    }
    return map;
  }, [roleConfiguredBuckets]);

  const roleDisplayName = roleDisplayNames?.[activeRole] ?? activeRole;

  // ---- Reset local state when overlay opens ----
  useEffect(() => {
    if (isOpen) {
      setRole(isEdit ? editingRole ?? '' : '');
      setSavingBuckets(new Set());
      setSavingSubBuckets(new Set());
      setExpandedBuckets(new Set());
    }
  }, [isOpen, isEdit, editingRole]);

  useEffect(() => {
    if (roleConfiguredBuckets.length > 0) {
      setExpandedBuckets(new Set(roleConfiguredBuckets.map(b => b.bucketName)));
    }
  }, [roleConfiguredBuckets]);

  const handleBucketToggle = useCallback(
    async (bucketName: string, enable: boolean) => {
      if (!activeRole) return;

      setSavingBuckets(prev => new Set(prev).add(bucketName));

      try {
        if (enable) {
          const bucket = buckets.find(b => b.bucketName === bucketName);
          await saveMutation.mutateAsync({
            role: activeRole,
            bucketName,
            subBucketNames:
              bucket?.subBuckets?.map(sb => sb.subBucketName) ?? []
          });

          toast({
            variant: 'success',
            title: 'Workflow activated successfully',
            isClosable: true
          });
        } else {
          const bucket = roleConfiguredBuckets.find(
            b => b.bucketName === bucketName
          );
          await deleteMutation.mutateAsync({
            bucketId: bucket?.bucketId ?? ''
          });
          toast({
            variant: 'success',
            title: 'Workflow deactivated successfully',
            isClosable: true
          });
        }

        if (enable) {
          setExpandedBuckets(prev => new Set(prev).add(bucketName));
        }
      } catch {
        toast({
          variant: 'error',
          title: `Failed to update workflow`,
          isClosable: true
        });
      } finally {
        setSavingBuckets(prev => {
          const next = new Set(prev);
          next.delete(bucketName);
          return next;
        });
      }
    },
    [activeRole, buckets, saveMutation, deleteMutation, toast]
  );

  const handleSubBucketToggle = useCallback(
    async (bucketName: string, subBucketName: string, enable: boolean) => {
      if (!activeRole) return;

      const subKey = `${bucketName}::${subBucketName}`;
      setSavingSubBuckets(prev => new Set(prev).add(subKey));

      try {
        const currentSet = new Set(
          configuredSubBucketsMap.get(bucketName) ?? []
        );

        if (enable) {
          currentSet.add(subBucketName);
        } else {
          currentSet.delete(subBucketName);
        }

        if (currentSet.size > 0) {
          await saveMutation.mutateAsync({
            role: activeRole,
            bucketName,
            subBucketNames: Array.from(currentSet)
          });

          toast({
            variant: 'success',
            title: 'Workflow updated successfully',
            isClosable: true
          });
        } else {
          const bucket = roleConfiguredBuckets.find(
            b => b.bucketName === bucketName
          );
          await deleteMutation.mutateAsync({
            bucketId: bucket?.bucketId ?? ''
          });
          toast({
            variant: 'success',
            title: 'Workflow deactivated successfully',
            isClosable: true
          });
        }
      } catch {
        toast({
          variant: 'error',
          title: `Failed to update workflow`,
          isClosable: true
        });
      } finally {
        setSavingSubBuckets(prev => {
          const next = new Set(prev);
          next.delete(subKey);
          return next;
        });
      }
    },
    [activeRole, configuredSubBucketsMap, saveMutation, deleteMutation, toast]
  );

  const handleExpandToggle = useCallback((bucketName: string) => {
    setExpandedBuckets(prev => {
      const next = new Set(prev);
      if (next.has(bucketName)) {
        next.delete(bucketName);
      } else {
        next.add(bucketName);
      }
      return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    setRole('');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <Overlay height="100%">
      <Flex
        flexDirection="column"
        backgroundColor="surfaceSecondaryFill"
        minH="100vh"
      >
        {/* ---- Scrollable content ---- */}
        <Flex
          direction="column"
          gap={6}
          mx={{ base: 4, md: 8 }}
          mt={{ base: 4, md: 8 }}
          paddingBottom="220px"
        >
          {/* ---- Header ---- */}
          <Flex direction="column" gap={1}>
            <Text textStyle="headingSection">Workflows</Text>
            <Text textStyle="textBody" color="textSecondary">
              Configure workflow buckets for <strong>{roleDisplayName}</strong>
            </Text>
          </Flex>

          {/* ---- Loading spinner ---- */}
          {isDataLoading && (
            <Flex justify="center" align="center" paddingY={20}>
              <Spinner color="#1D4ED8" thickness="3px" />
            </Flex>
          )}

          {/* ---- Loaded: empty state ---- */}
          {activeRole && !isDataLoading && buckets.length === 0 && (
            <OutlineCard borderless paddingX={4} paddingY={6}>
              <Flex
                flexDirection="column"
                gap={2}
                alignItems="center"
                paddingY={8}
              >
                <Text textStyle="headingGroup">No workflows available</Text>
                <Text textStyle="textBody" color="textSecondary">
                  No workflow buckets are available for this role.
                </Text>
              </Flex>
            </OutlineCard>
          )}

          {/* ---- Loaded: toggle list ---- */}
          {activeRole && !isDataLoading && buckets.length > 0 && (
            <BucketToggleList
              buckets={buckets}
              configuredBucketNames={configuredBucketNames}
              configuredSubBucketsMap={configuredSubBucketsMap}
              savingBuckets={savingBuckets}
              savingSubBuckets={savingSubBuckets}
              expandedBuckets={expandedBuckets}
              onBucketToggle={handleBucketToggle}
              onSubBucketToggle={handleSubBucketToggle}
              onExpandToggle={handleExpandToggle}
            />
          )}
        </Flex>

        {/* ---- Sticky footer: Exit button ---- */}
        <Flex
          padding={4}
          bottom={0}
          zIndex={1}
          justifyContent="flex-end"
          position="sticky"
          bgColor="surfacePrimaryFill"
          marginX="auto"
          width="100%"
        >
          <Button variant="caution" onClick={handleClose}>
            Exit
          </Button>
        </Flex>
      </Flex>
    </Overlay>
  );
};

export default BucketFormModal;
